"use client"

import { useEffect, useState, useCallback } from "react"
import { useNavigate, useOutletContext } from "react-router-dom"
import { Link } from "react-router-dom"
import Footer from "../components/Footer"
import { fetchPorts, fetchCabins, fetchPayloadTypes, searchTrips } from "../api/portApi"
import { fetchCompanySiteBookingPermissions } from "../api/companySitePermissionApi"

const BOOKING_TABS = ["passenger", "vehicle", "cargo"]
const VISA_TYPES = [
  { value: "All", label: "All" },
  { value: "Resident", label: "Resident" },
  { value: "Student", label: "Student" },
  { value: "Visit", label: "Visit" },
  { value: "Business", label: "Business" },
  { value: "Tourist", label: "Tourist" },
  { value: "Special", label: "Special" },
  { value: "Medical", label: "Medical" },
  { value: "Government", label: "Government" },
]

const CompanySite = () => {
  const navigate = useNavigate()
  const { companyInfo, isCompanyAuth } = useOutletContext()

  // Tab state
  const [activeMainTab, setActiveMainTab] = useState("passenger") // passenger, vehicle, cargo
  const [activeSubTab, setActiveSubTab] = useState("oneway") // oneway, return
  const [allowedMainTabs, setAllowedMainTabs] = useState([])
  const [permissionLoading, setPermissionLoading] = useState(true)
  const [permissionError, setPermissionError] = useState("")

  // API data state
  const [ports, setPorts] = useState([])
  const [cabins, setCabins] = useState([])
  const [payloadTypes, setPayloadTypes] = useState([])
  const [loadingPorts, setLoadingPorts] = useState(false)
  const [loadingCabins, setLoadingCabins] = useState(false)
  const [loadingPayloadTypes, setLoadingPayloadTypes] = useState(false)

  // Form state - Passenger
  const [paxOriginPort, setPaxOriginPort] = useState("")
  const [paxDestPort, setPaxDestPort] = useState("")
  const [paxDepartDate, setPaxDepartDate] = useState("")
  const [paxReturnDate, setPaxReturnDate] = useState("")
  const [paxCabin, setPaxCabin] = useState("")
  const [paxVisaType, setPaxVisaType] = useState("All")
  const [passengerCounts, setPassengerCounts] = useState({}) // Dynamic passenger counts by payload type ID
  const [showPaxDropdown, setShowPaxDropdown] = useState(false)

  // Form state - Vehicle
  const [vehOriginPort, setVehOriginPort] = useState("")
  const [vehDestPort, setVehDestPort] = useState("")
  const [vehDepartDate, setVehDepartDate] = useState("")
  const [vehReturnDate, setVehReturnDate] = useState("")
  const [vehVisaType, setVehVisaType] = useState("All")
  const [vehCabin, setVehCabin] = useState("")

  // Form state - Cargo
  const [cargoOriginPort, setCargoOriginPort] = useState("")
  const [cargoDestPort, setCargoDestPort] = useState("")
  const [cargoDepartDate, setCargoDepartDate] = useState("")
  const [cargoReturnDate, setCargoReturnDate] = useState("")
  const [cargoVisaType, setCargoVisaType] = useState("All")
  const [cargoCabin, setCargoCabin] = useState("")
  const [cargoQuantity, setCargoQuantity] = useState(1)

  // Get token from localStorage - companyAuthToken comes from URL, authToken from B2C login
  const getToken = () => {
    return localStorage.getItem("companyAuthToken") || localStorage.getItem("authToken") || ""
  }

  // Fetch ports
  const loadPorts = useCallback(async () => {
    const token = getToken()
    if (!token) return

    setLoadingPorts(true)
    try {
      const portsData = await fetchPorts(token)
      setPorts(portsData)
      
      // Set default values if ports exist
      if (portsData.length > 0) {
        setPaxOriginPort(portsData[0]._id)
        setVehOriginPort(portsData[0]._id)
        setCargoOriginPort(portsData[0]._id)
        
        if (portsData.length > 1) {
          setPaxDestPort(portsData[1]._id)
          setVehDestPort(portsData[1]._id)
          setCargoDestPort(portsData[1]._id)
        }
      }
    } catch (error) {
      console.error("Failed to load ports:", error)
    } finally {
      setLoadingPorts(false)
    }
  }, [])

  // Fetch cabins based on active tab type
  const loadCabins = useCallback(async (type) => {
    const token = getToken()
    if (!token) return

    setLoadingCabins(true)
    try {
      const cabinsData = await fetchCabins(token, type, 1, 100)
      setCabins(cabinsData)
      
      // Set default cabin value if cabins exist
      if (cabinsData.length > 0) {
        setPaxCabin(cabinsData[0]._id)
        setVehCabin(cabinsData[0]._id)
        setCargoCabin(cabinsData[0]._id)
      }
    } catch (error) {
      console.error("Failed to load cabins:", error)
    } finally {
      setLoadingCabins(false)
    }
  }, [])

  // Fetch payload types (only for passenger category)
  const loadPayloadTypes = useCallback(async (category) => {
    const token = getToken()
    if (!token) return

    setLoadingPayloadTypes(true)
    try {
      const typesData = await fetchPayloadTypes(token, category, 1, 100)
      setPayloadTypes(typesData)
      
      // Initialize passenger counts with default values
      if (typesData.length > 0) {
        const initialCounts = {}
        typesData.forEach((type) => {
          if (category === 'passenger') {
            const isAdult = type.name.toLowerCase().includes('adult') || type.ageRange?.from >= 12
            initialCounts[type._id] = isAdult ? 1 : 0
          } else {
            // For vehicle and cargo, we can default the first type to 1 for convenience
            const index = typesData.findIndex(t => t._id === type._id)
            initialCounts[type._id] = index === 0 ? 1 : 0
          }
        })
        setPassengerCounts(initialCounts)
      }
    } catch (error) {
      console.error("Failed to load payload types:", error)
    } finally {
      setLoadingPayloadTypes(false)
    }
  }, [])

  // Load ports and payload types on mount
  useEffect(() => {
    loadPorts()
  }, [loadPorts])

  // Website booking permissions apply only to authenticated B2B/company sessions.
  useEffect(() => {
    let active = true

    const loadBookingTabPermissions = async () => {
      setPermissionLoading(true)
      setPermissionError("")

      const urlToken = new URLSearchParams(window.location.search).get("token")
      const companyToken = urlToken || localStorage.getItem("companyAuthToken")

      if (!companyToken) {
        if (active) {
          setAllowedMainTabs(BOOKING_TABS)
          setPermissionLoading(false)
        }
        return
      }

      try {
        const permissionData = await fetchCompanySiteBookingPermissions(companyToken)
        const permittedTabs = BOOKING_TABS.filter((tab) => permissionData.allowedTabs.includes(tab))

        if (active) setAllowedMainTabs(permittedTabs)
      } catch (error) {
        if (active) {
          setAllowedMainTabs([])
          setPermissionError(error.message)
        }
      } finally {
        if (active) setPermissionLoading(false)
      }
    }

    loadBookingTabPermissions()
    return () => {
      active = false
    }
  }, [isCompanyAuth])

  useEffect(() => {
    if (permissionLoading) return

    const params = new URLSearchParams(window.location.search)
    const requestedTab = params.get("tab")?.toLowerCase()
    const nextTab = requestedTab && allowedMainTabs.includes(requestedTab)
      ? requestedTab
      : allowedMainTabs.includes(activeMainTab)
        ? activeMainTab
        : allowedMainTabs[0] || ""

    if (nextTab !== activeMainTab) setActiveMainTab(nextTab)
  }, [activeMainTab, allowedMainTabs, permissionLoading])

  // Load cabins and payload types when main tab changes
  useEffect(() => {
    if (permissionLoading || !allowedMainTabs.includes(activeMainTab)) return
    loadCabins(activeMainTab)
    loadPayloadTypes(activeMainTab)
  }, [activeMainTab, allowedMainTabs, permissionLoading, loadCabins, loadPayloadTypes])

  // Close passenger dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const dropdown = document.getElementById("passenger-dropdown-custom")
      const summary = document.getElementById("passenger-summary-custom")
      if (dropdown && summary && !dropdown.contains(e.target) && !summary.contains(e.target)) {
        setShowPaxDropdown(false)
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  // Handle main tab change
  const handleMainTabChange = (tab) => {
    setActiveMainTab(tab)
    setActiveSubTab("oneway")
  }

  // State for loading and errors
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState("")

  // Handle form submissions
  const handlePassengerSubmit = async (e) => {
    e.preventDefault()
    setSearchLoading(true)
    setSearchError("")

    try {
      const token = getToken()
      if (!token) {
        setSearchError("Please login to search trips")
        setSearchLoading(false)
        return
      }

      // Build passengers array with payloadTypeId and quantity
      const passengersPayload = Object.entries(passengerCounts)
        .filter(([_, quantity]) => quantity > 0)
        .map(([payloadTypeId, quantity]) => ({
          payloadTypeId,
          quantity,
        }))

      if (passengersPayload.length === 0) {
        setSearchError("Please select at least one passenger")
        setSearchLoading(false)
        return
      }

      const searchParams = {
        category: "passenger",
        tripType: activeSubTab === "oneway" ? "one_way" : "return",
        originPort: paxOriginPort,
        destinationPort: paxDestPort,
        departureDate: paxDepartDate,
        cabin: paxCabin,
        visaType: paxVisaType,
        passengers: passengersPayload,
      }

      // Add return date if it's a return trip
      if (activeSubTab === "return" && paxReturnDate) {
        searchParams.returnDate = paxReturnDate
      }

      const result = await searchTrips(token, searchParams)

      // Navigate to results page with the search results
      navigate("search-result-passenger", {
        state: {
          searchResults: result,
          searchParams: searchParams,
          ports: ports,
          cabins: cabins,
          payloadTypes: payloadTypes,
        },
      })
    } catch (error) {
      console.error("Search error:", error)
      setSearchError("Failed to search trips. Please try again.")
    } finally {
      setSearchLoading(false)
    }
  }

  const handleVehicleSubmit = async (e) => {
    e.preventDefault()
    setSearchLoading(true)
    setSearchError("")

    try {
      const token = getToken()
      if (!token) {
        setSearchError("Please login to search trips")
        setSearchLoading(false)
        return
      }

      const payloadItems = Object.entries(passengerCounts)
        .filter(([_, quantity]) => quantity > 0)
        .map(([payloadTypeId, quantity]) => ({
          payloadTypeId,
          quantity,
        }))

      if (payloadItems.length === 0) {
        setSearchError("Please select at least one vehicle")
        setSearchLoading(false)
        return
      }


      const searchParams = {
        category: "vehicle",
        tripType: activeSubTab === "oneway" ? "one_way" : "return",
        originPort: vehOriginPort,
        destinationPort: vehDestPort,
        departureDate: vehDepartDate,
        cabin: vehCabin,
        visaType: vehVisaType,
        passengers: payloadItems,
      }

      if (activeSubTab === "return" && vehReturnDate) {
        searchParams.returnDate = vehReturnDate
      }

      const result = await searchTrips(token, searchParams)

      navigate("search-result-vehicle", {
        state: {
          searchResults: result,
          searchParams: searchParams,
          ports: ports,
          cabins: cabins,
          payloadTypes: payloadTypes,
        },
      })
    } catch (error) {
      console.error("Search error:", error)
      setSearchError("Failed to search trips. Please try again.")
    } finally {
      setSearchLoading(false)
    }
  }

  const handleCargoSubmit = async (e) => {
    e.preventDefault()
    setSearchLoading(true)
    setSearchError("")

    try {
      const token = getToken()
      if (!token) {
        setSearchError("Please login to search trips")
        setSearchLoading(false)
        return
      }

      const payloadItems = Object.entries(passengerCounts)
        .filter(([_, quantity]) => quantity > 0)
        .map(([payloadTypeId, quantity]) => ({
          payloadTypeId,
          quantity,
        }))

      if (payloadItems.length === 0) {
        setSearchError("Please select at least one cargo item")
        setSearchLoading(false)
        return
      }

      const searchParams = {
        category: "cargo",
        tripType: activeSubTab === "oneway" ? "one_way" : "return",
        originPort: cargoOriginPort,
        destinationPort: cargoDestPort,
        departureDate: cargoDepartDate,
        cabin: cargoCabin,
        visaType: cargoVisaType,
        passengers: payloadItems,
      }

      if (activeSubTab === "return" && cargoReturnDate) {
        searchParams.returnDate = cargoReturnDate
      }

      const result = await searchTrips(token, searchParams)

      navigate("search-result-cargo", {
        state: {
          searchResults: result,
          searchParams: searchParams,
          ports: ports,
          cabins: cabins,
          payloadTypes: payloadTypes,
        },
      })
    } catch (error) {
      console.error("Search error:", error)
      setSearchError("Failed to search trips. Please try again.")
    } finally {
      setSearchLoading(false)
    }
  }

  // Passenger count helpers
  const getPassengerSummary = () => {
    if (payloadTypes.length === 0) return "Select passengers"
    
    const summaryParts = payloadTypes.map((type) => {
      const count = passengerCounts[type._id] || 0
      return `${count} ${type.name}`
    })
    
    return summaryParts.join(", ")
  }

  // Update passenger count
  const updatePassengerCount = (typeId, delta) => {
    setPassengerCounts((prev) => {
      const currentCount = prev[typeId] || 0
      const newCount = Math.max(0, currentCount + delta)
      
      return { ...prev, [typeId]: newCount }
    })
  }

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="container-lg">
          <div className="row g-3 justify-content-center">
            <div className="col-lg-8">
              <h1 style={{ textTransform: "uppercase" }} className="fw-bold topheadings">
                Your Journey, <span className="gradient-word">Simplified.</span>
              </h1>
              <p>
                Discover, book, and manage your ferry travel seamlessly. Our platform connects you to a world of ferry
                routes, whether you're a passenger, a business with cargo, or traveling with your vehicle.
              </p>

              {/* Promo banner - hidden for now */}
              {/* <div className="promo mt-4">
                <div className="col-md-12">
                  <h5 style={{ textTransform: "uppercase" }} className="fw-bold">
                    Exclusive Summer Deals!
                  </h5>
                  <p className="mb-3">Book your trip now and get up to 30% off on all routes. Limited time offer!</p>

                  <Link
                    to="#"
                    className="btn btn-turquoise btn-primary"
                    style={{ height: "auto", padding: "10px 30px" }}
                  >
                    Find Deals
                  </Link>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING + TABS */}
      <section className="features py-5">
        <div className="container-lg">
          <div className="row g-3 justify-content-center">
            <div className="col-lg-8">
              <div className="booking-wrap mt-4 mx-auto">
                {/* Main Tabs */}
                {permissionLoading && (
                  <div className="alert alert-info text-center" role="status">
                    Loading booking permissions...
                  </div>
                )}
                {!permissionLoading && permissionError && (
                  <div className="alert alert-danger text-center" role="alert">
                    Unable to load booking permissions: {permissionError}
                  </div>
                )}
                {!permissionLoading && !permissionError && allowedMainTabs.length === 0 && (
                  <div className="alert alert-warning text-center" role="alert">
                    You do not have permission to issue tickets on the company site.
                  </div>
                )}
                <ul className="nav nav-pills justify-content-center mb-4">
                  {allowedMainTabs.includes("passenger") && <li className="nav-item">
                    <button
                      className={`nav-link ${activeMainTab === "passenger" ? "active" : ""}`}
                      onClick={() => handleMainTabChange("passenger")}
                    >
                      <i className="bi bi-person-fill"></i> Passenger
                    </button>
                  </li>}
                  {allowedMainTabs.includes("vehicle") && <li className="nav-item">
                    <button
                      className={`nav-link ${activeMainTab === "vehicle" ? "active" : ""}`}
                      onClick={() => handleMainTabChange("vehicle")}
                    >
                      <i className="bi bi-car-front-fill"></i> Vehicle
                    </button>
                  </li>}
                  {allowedMainTabs.includes("cargo") && <li className="nav-item">
                    <button
                      className={`nav-link ${activeMainTab === "cargo" ? "active" : ""}`}
                      onClick={() => handleMainTabChange("cargo")}
                    >
                      <i className="bi bi-box-seam-fill"></i> Cargo
                    </button>
                  </li>}
                </ul>

                <div className="tab-content">
                  {/* Passenger Tab */}
                  {allowedMainTabs.includes("passenger") && activeMainTab === "passenger" && (
                    <div className="tab-pane fade show active">
                      <ul className="nav nav-pills mb-3 justify-content-center">
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeSubTab === "oneway" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("oneway")}
                          >
                            One-Way
                          </button>
                        </li>
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeSubTab === "return" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("return")}
                          >
                            Return
                          </button>
                        </li>
                      </ul>

                      <form className="row g-3" onSubmit={handlePassengerSubmit}>
                        <div className="col-md-6">
                          <label className="form-label">Origin Port</label>
                          <select
                            className="form-select"
                            value={paxOriginPort}
                            onChange={(e) => setPaxOriginPort(e.target.value)}
                            disabled={loadingPorts}
                          >
                            {loadingPorts ? (
                              <option>Loading...</option>
                            ) : ports.length === 0 ? (
                              <option>No ports available</option>
                            ) : (
                              ports.map((port) => (
                                <option key={port._id} value={port._id}>
                                  {port.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Destination Port</label>
                          <select
                            className="form-select"
                            value={paxDestPort}
                            onChange={(e) => setPaxDestPort(e.target.value)}
                            disabled={loadingPorts}
                          >
                            {loadingPorts ? (
                              <option>Loading...</option>
                            ) : ports.length === 0 ? (
                              <option>No ports available</option>
                            ) : (
                              ports.map((port) => (
                                <option key={port._id} value={port._id}>
                                  {port.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Departure Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={paxDepartDate}
                            onChange={(e) => setPaxDepartDate(e.target.value)}
                          />
                        </div>
                        {activeSubTab === "return" && (
                          <div className="col-md-6">
                            <label className="form-label">Return Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={paxReturnDate}
                              onChange={(e) => setPaxReturnDate(e.target.value)}
                            />
                          </div>
                        )}
                        <div className="col-md-6">
                          <label className="form-label">Cabin</label>
                          <select
                            className="form-select"
                            value={paxCabin}
                            onChange={(e) => setPaxCabin(e.target.value)}
                            disabled={loadingCabins}
                          >
                            {loadingCabins ? (
                              <option>Loading...</option>
                            ) : cabins.length === 0 ? (
                              <option>No cabins available</option>
                            ) : (
                              cabins.map((cabin) => (
                                <option key={cabin._id} value={cabin._id}>
                                  {cabin.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Visa Type</label>
                          <select
                            className="form-select"
                            value={paxVisaType}
                            onChange={(e) => setPaxVisaType(e.target.value)}
                          >
                            {VISA_TYPES.map((visa) => (
                              <option key={visa.value} value={visa.value}>
                                {visa.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Passenger selector */}
                        <div className="col-md-12 position-relative">
                          <label className="form-label">Passengers</label>
                          <input
                            type="text"
                            id="passenger-summary-custom"
                            className="form-control"
                            value={getPassengerSummary()}
                            readOnly
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowPaxDropdown(!showPaxDropdown)
                            }}
                            style={{ cursor: "pointer" }}
                          />

                          {showPaxDropdown && (
                            <div
                              id="passenger-dropdown-custom"
                              className="card bg-white shadow position-absolute w-100 mt-1"
                              style={{ zIndex: 1000 }}
                            >
                              <div className="card-body">
                                {loadingPayloadTypes ? (
                                  <div className="text-center">Loading...</div>
                                ) : payloadTypes.length === 0 ? (
                                  <div className="text-center text-muted">No passenger types available</div>
                                ) : (
                                  payloadTypes.map((type, index) => (
                                    <div
                                      key={type._id}
                                      className={`d-flex justify-content-between align-items-center ${
                                        index < payloadTypes.length - 1 ? "mb-2" : ""
                                      }`}
                                    >
                                      <span>{type.name}</span>
                                      <div className="input-group input-group-sm" style={{ width: "120px" }}>
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => updatePassengerCount(type._id, -1)}
                                        >
                                          -
                                        </button>
                                        <input
                                          type="text"
                                          className="form-control text-center"
                                          value={passengerCounts[type._id] || 0}
                                          readOnly
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => updatePassengerCount(type._id, 1)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {searchError && (
                          <div className="col-12">
                            <div className="alert alert-danger" role="alert">
                              {searchError}
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <button
                            type="submit"
                            className="btn btn-turquoise btn-primary w-100"
                            disabled={searchLoading}
                          >
                            {searchLoading ? "Searching..." : "Search"}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Vehicle Tab */}
                  {allowedMainTabs.includes("vehicle") && activeMainTab === "vehicle" && (
                    <div className="tab-pane fade show active">
                      <ul className="nav nav-pills mb-3 justify-content-center">
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeSubTab === "oneway" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("oneway")}
                          >
                            One-Way
                          </button>
                        </li>
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeSubTab === "return" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("return")}
                          >
                            Return
                          </button>
                        </li>
                      </ul>

                      <form className="row g-3" onSubmit={handleVehicleSubmit}>
                        <div className="col-md-6">
                          <label className="form-label">Origin Port</label>
                          <select
                            className="form-select"
                            value={vehOriginPort}
                            onChange={(e) => setVehOriginPort(e.target.value)}
                            disabled={loadingPorts}
                          >
                            {loadingPorts ? (
                              <option>Loading...</option>
                            ) : ports.length === 0 ? (
                              <option>No ports available</option>
                            ) : (
                              ports.map((port) => (
                                <option key={port._id} value={port._id}>
                                  {port.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Destination Port</label>
                          <select
                            className="form-select"
                            value={vehDestPort}
                            onChange={(e) => setVehDestPort(e.target.value)}
                            disabled={loadingPorts}
                          >
                            {loadingPorts ? (
                              <option>Loading...</option>
                            ) : ports.length === 0 ? (
                              <option>No ports available</option>
                            ) : (
                              ports.map((port) => (
                                <option key={port._id} value={port._id}>
                                  {port.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Cabin</label>
                          <select
                            className="form-select"
                            value={vehCabin}
                            onChange={(e) => setVehCabin(e.target.value)}
                            disabled={loadingCabins}
                          >
                            {loadingCabins ? (
                              <option>Loading...</option>
                            ) : cabins.length === 0 ? (
                              <option>No cabins available</option>
                            ) : (
                              cabins.map((cabin) => (
                                <option key={cabin._id} value={cabin._id}>
                                  {cabin.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Departure Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={vehDepartDate}
                            onChange={(e) => setVehDepartDate(e.target.value)}
                          />
                        </div>
                        {activeSubTab === "return" && (
                          <div className="col-md-6">
                            <label className="form-label">Return Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={vehReturnDate}
                              onChange={(e) => setVehReturnDate(e.target.value)}
                            />
                          </div>
                        )}
                        <div className="col-md-12 position-relative">
                          <label className="form-label">Vehicle Type & Quantity</label>
                          <input
                            type="text"
                            className="form-control"
                            value={getPassengerSummary()}
                            readOnly
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowPaxDropdown(!showPaxDropdown)
                            }}
                            style={{ cursor: "pointer" }}
                          />

                          {showPaxDropdown && (
                            <div
                              className="card bg-white shadow position-absolute w-100 mt-1"
                              style={{ zIndex: 1000 }}
                            >
                              <div className="card-body">
                                {loadingPayloadTypes ? (
                                  <div className="text-center">Loading...</div>
                                ) : payloadTypes.length === 0 ? (
                                  <div className="text-center text-muted">No vehicle types available</div>
                                ) : (
                                  payloadTypes.map((type, index) => (
                                    <div
                                      key={type._id}
                                      className={`d-flex justify-content-between align-items-center ${
                                        index < payloadTypes.length - 1 ? "mb-2" : ""
                                      }`}
                                    >
                                      <span>{type.name}</span>
                                      <div className="input-group input-group-sm" style={{ width: "120px" }}>
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => updatePassengerCount(type._id, -1)}
                                        >
                                          -
                                        </button>
                                        <input
                                          type="text"
                                          className="form-control text-center"
                                          value={passengerCounts[type._id] || 0}
                                          readOnly
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => updatePassengerCount(type._id, 1)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Visa Type</label>
                          <select
                            className="form-select"
                            value={vehVisaType}
                            onChange={(e) => setVehVisaType(e.target.value)}
                          >
                            {VISA_TYPES.map((visa) => (
                              <option key={visa.value} value={visa.value}>
                                {visa.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {searchError && (
                          <div className="col-12">
                            <div className="alert alert-danger" role="alert">
                              {searchError}
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <button
                            type="submit"
                            className="btn btn-turquoise btn-primary w-100"
                            disabled={searchLoading}
                          >
                            {searchLoading ? "Searching..." : "Search"}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Cargo Tab */}
                  {allowedMainTabs.includes("cargo") && activeMainTab === "cargo" && (
                    <div className="tab-pane fade show active">
                      <ul className="nav nav-pills mb-3 justify-content-center">
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeSubTab === "oneway" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("oneway")}
                          >
                            One-Way
                          </button>
                        </li>
                        {/* Return option hidden for Cargo — cargo is one-way only */}
                      </ul>


                      <form className="row g-3" onSubmit={handleCargoSubmit}>
                        <div className="col-md-6">
                          <label className="form-label">Origin Port</label>
                          <select
                            className="form-select"
                            value={cargoOriginPort}
                            onChange={(e) => setCargoOriginPort(e.target.value)}
                            disabled={loadingPorts}
                          >
                            {loadingPorts ? (
                              <option>Loading...</option>
                            ) : ports.length === 0 ? (
                              <option>No ports available</option>
                            ) : (
                              ports.map((port) => (
                                <option key={port._id} value={port._id}>
                                  {port.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Destination Port</label>
                          <select
                            className="form-select"
                            value={cargoDestPort}
                            onChange={(e) => setCargoDestPort(e.target.value)}
                            disabled={loadingPorts}
                          >
                            {loadingPorts ? (
                              <option>Loading...</option>
                            ) : ports.length === 0 ? (
                              <option>No ports available</option>
                            ) : (
                              ports.map((port) => (
                                <option key={port._id} value={port._id}>
                                  {port.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Cabin</label>
                          <select
                            className="form-select"
                            value={cargoCabin}
                            onChange={(e) => setCargoCabin(e.target.value)}
                            disabled={loadingCabins}
                          >
                            {loadingCabins ? (
                              <option>Loading...</option>
                            ) : cabins.length === 0 ? (
                              <option>No cabins available</option>
                            ) : (
                              cabins.map((cabin) => (
                                <option key={cabin._id} value={cabin._id}>
                                  {cabin.name}
                                </option>
                              ))
                            )}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Departure Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={cargoDepartDate}
                            onChange={(e) => setCargoDepartDate(e.target.value)}
                          />
                        </div>
                        {activeSubTab === "return" && (
                          <div className="col-md-6">
                            <label className="form-label">Return Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={cargoReturnDate}
                              onChange={(e) => setCargoReturnDate(e.target.value)}
                            />
                          </div>
                        )}
                        <div className="col-md-6">
                          <label className="form-label">Visa Type</label>
                          <select
                            className="form-select"
                            value={cargoVisaType}
                            onChange={(e) => setCargoVisaType(e.target.value)}
                          >
                            {VISA_TYPES.map((visa) => (
                              <option key={visa.value} value={visa.value}>
                                {visa.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-12 position-relative mb-3">
                          <label className="form-label">Cargo Type & Quantity</label>
                          <input
                            type="text"
                            className="form-control"
                            value={getPassengerSummary()}
                            readOnly
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowPaxDropdown(!showPaxDropdown)
                            }}
                            style={{ cursor: "pointer" }}
                          />

                          {showPaxDropdown && (
                            <div
                              className="card bg-white shadow position-absolute w-100 mt-1"
                              style={{ zIndex: 1000 }}
                            >
                              <div className="card-body">
                                {loadingPayloadTypes ? (
                                  <div className="text-center">Loading...</div>
                                ) : payloadTypes.length === 0 ? (
                                  <div className="text-center text-muted">No cargo types available</div>
                                ) : (
                                  payloadTypes.map((type, index) => (
                                    <div
                                      key={type._id}
                                      className={`d-flex justify-content-between align-items-center ${
                                        index < payloadTypes.length - 1 ? "mb-2" : ""
                                      }`}
                                    >
                                      <span>{type.name}</span>
                                      <div className="input-group input-group-sm" style={{ width: "120px" }}>
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => updatePassengerCount(type._id, -1)}
                                        >
                                          -
                                        </button>
                                        <input
                                          type="text"
                                          className="form-control text-center"
                                          value={passengerCounts[type._id] || 0}
                                          readOnly
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          onClick={() => updatePassengerCount(type._id, 1)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {searchError && (
                          <div className="col-12">
                            <div className="alert alert-danger" role="alert">
                              {searchError}
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <button
                            type="submit"
                            className="btn btn-turquoise btn-primary w-100"
                            disabled={searchLoading}
                          >
                            {searchLoading ? "Searching..." : "Search"}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
                {/* /tab-content */}
              </div>
              {/* /booking-wrap */}
            </div>
          </div>
        </div>
      </section>

      {/* Hot Destinations + Offers */}
      <section className="py-5 features destination">
        <div className="container-lg">
          <h3 className="section-title">Hot Destinations</h3>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="info-card">
                <div className="d-flex align-items-center">
                  <img
                    className="tiny-img"
                    src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=200"
                    alt=""
                  />
                  <small className="text-secondary">A bustling port city</small>
                </div>
                <h5>Suakin</h5>
                <p>
                  Experience the vibrant culture and breathtaking views of this historic port city. A perfect getaway!
                </p>
                <Link className="link" to="#">
                  Explore Ferries →
                </Link>
              </div>
            </div>

            <div className="col-md-4">
              <div className="info-card">
                <div className="d-flex align-items-center">
                  <img
                    className="tiny-img"
                    src="https://images.unsplash.com/photo-1502082553048-f009c37129b9?q=80&w=200"
                    alt=""
                  />
                  <small className="text-secondary">A tropical island</small>
                </div>
                <h5>Port Sudan</h5>
                <p>
                  Discover pristine beaches and crystal-clear waters. Your ultimate relaxation destination is just a
                  ferry ride away.
                </p>
                <Link className="link" to="#">
                  Explore Ferries →
                </Link>
              </div>
            </div>

            <div className="col-md-4">
              <div className="info-card">
                <div className="d-flex align-items-center">
                  <img
                    className="tiny-img"
                    src="https://images.unsplash.com/photo-1503341455253-b2e723bb3dbb?q=80&w=200"
                    alt=""
                  />
                  <small className="text-secondary">A quaint coastal village</small>
                </div>
                <h5>Jeddah</h5>
                <p>
                  A charming and historic city awaits. Explore the local markets and enjoy the tranquility of coastal
                  life.
                </p>
                <Link className="link" to="#">
                  Explore Ferries →
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="container-lg">
          <h3 className="section-title">Offers &amp; Promotions</h3>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="info-card">
                <div className="d-flex align-items-center">
                  <img
                    className="tiny-img"
                    src="https://images.unsplash.com/photo-1503341455253-b2e723bb3dbb?q=80&w=200"
                    alt=""
                  />
                  <small className="text-secondary">A family on a ferry</small>
                  <span className="ms-auto deal-badge deal-sale">SALE</span>
                </div>
                <h5>Family Package Deal</h5>
                <p>Bring the whole family and get 50% off for kids under 12. Perfect for your next family adventure!</p>
                <Link className="link" to="#">
                  See Details →
                </Link>
              </div>
            </div>

            <div className="col-md-4">
              <div className="info-card">
                <div className="d-flex align-items-center">
                  <img
                    className="tiny-img"
                    src="https://images.unsplash.com/photo-1502082553048-f009c37129b9?q=80&w=200"
                    alt=""
                  />
                  <small className="text-secondary">A car on a ferry</small>
                  <span className="ms-auto deal-badge deal-off">20% OFF</span>
                </div>
                <h5>Vehicle Transport Discount</h5>
                <p>Traveling with your vehicle? Get a 20% discount on all vehicle bookings this month.</p>
                <Link className="link" to="#">
                  Book Now →
                </Link>
              </div>
            </div>

            <div className="col-md-4">
              <div className="info-card">
                <div className="d-flex align-items-center">
                  <img
                    className="tiny-img"
                    src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=200"
                    alt=""
                  />
                  <small className="text-secondary">A cargo ship at sunset</small>
                  <span className="ms-auto deal-badge deal-promo">PROMO</span>
                </div>
                <h5>Bulk Cargo Savings</h5>
                <p>Ship large quantities and save big. Get special rates for bulk cargo shipments.</p>
                <Link className="link" to="#">
                  Learn More →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default CompanySite
